import { AtlasUserOverride } from 'api/atlasClient'
import { CerebroPaginatedResponse } from 'api/Cerebro/types'
import {
    Expression,
    HulkQuery,
    HulkQuerySortExpression,
    queryHulkDatasource,
    toCerebroPaginatedEnvelope,
} from 'api/hulkApi'
import { SEARCH_RESULTS_PER_QUERY } from 'configuration/typeahead'

import {
    buildMarketAnalysisSegmentBaseFilters,
    intersectExpressions,
} from './marketAnalysisSegmentsFilters'
import { MARKET_ANALYSIS_SEGMENTS_DATA_SOURCE } from './marketAnalysisSegmentsWidgetApi'
import { buildBrandSearchPredicate } from './predicates'

const BRAND_DIMENSION = 'brand_stable'
const REVENUE_METRIC = 'revenue_total_sum'

export interface SegmentBrandRevenueRow {
    brand_stable?: string
    revenue_total_sum?: number | string
}

interface SegmentDateRange {
    reportDateMin?: string
    reportDateMax?: string
}

export function emptySegmentEnvelope<T>(): CerebroPaginatedResponse<T> {
    return {
        data: { count: 0, next: null, previous: null, results: [] },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {} as never,
    }
}

interface QuerySegmentDataOptions {
    segmentIds: string[]
    dimensions?: string[]
    metrics?: string[]
    ordering?: HulkQuerySortExpression[]
    predicate?: Expression
    dateRange?: SegmentDateRange
    limit?: number
    // Impersonation override for the print page — scopes the Atlas token to the
    // dashboard owner. See AtlasUserOverride.
    override?: AtlasUserOverride
}

// Low-level segment-scoped read against the sales_estimates datasource for form
// flows. Intentionally lighter than the dashboard widget query: no top-N, period
// comparison, count, or metric/record translation — so it's safe only for
// suffix-less metrics (e.g. revenue_total_sum) and callers that don't need
// marketplace-stamped rows.
export async function querySegmentData<T>({
    segmentIds,
    dimensions = [],
    metrics = [],
    ordering,
    predicate,
    dateRange,
    limit = SEARCH_RESULTS_PER_QUERY,
    override,
}: QuerySegmentDataOptions): Promise<CerebroPaginatedResponse<T>> {
    if (segmentIds.length === 0) {
        return emptySegmentEnvelope<T>()
    }
    const { expression: baseFilters } =
        await buildMarketAnalysisSegmentBaseFilters(segmentIds, override)
    const filters = intersectExpressions([baseFilters, predicate])
    // Same alignment as the widget query. This reads the same weekly rows, and
    // callers hand it the widget's own dates — the brand and seller pickers rank
    // and threshold their options on revenue from it. Left unaligned it would
    // measure a different set of weeks than the widget beside it, so an option
    // could be ordered or dropped on revenue the widget never shows.
    const time =
        dateRange?.reportDateMin || dateRange?.reportDateMax
            ? {
                  start: dateRange.reportDateMin,
                  end: dateRange.reportDateMax,
                  align_to_grain: true,
              }
            : undefined
    const query: HulkQuery = {
        dimensions,
        metrics,
        filters,
        ordering,
        time,
        pagination: { limit, offset: 0, include_count: false },
    }
    const response = await queryHulkDatasource<T>(
        MARKET_ANALYSIS_SEGMENTS_DATA_SOURCE,
        query,
        override
    )
    return toCerebroPaginatedEnvelope(response)
}

const revenueOf = (row: SegmentBrandRevenueRow | undefined): number => {
    const value = Number(row?.[REVENUE_METRIC])
    return Number.isFinite(value) ? value : 0
}

// Brands in a segment with revenue, ranked descending. Optional substring search
// and date window; omitting the window covers the segment's entire history.
export function fetchSegmentBrandRevenue({
    segmentId,
    query,
    reportDateMin,
    reportDateMax,
    limit,
}: {
    segmentId: string
    query?: string
    reportDateMin?: string
    reportDateMax?: string
    limit?: number
}): Promise<CerebroPaginatedResponse<SegmentBrandRevenueRow>> {
    return querySegmentData<SegmentBrandRevenueRow>({
        segmentIds: segmentId ? [segmentId] : [],
        dimensions: [BRAND_DIMENSION],
        metrics: [REVENUE_METRIC],
        ordering: [{ field: REVENUE_METRIC, direction: 'desc' }],
        predicate: buildBrandSearchPredicate(query),
        dateRange: { reportDateMin, reportDateMax },
        limit,
    })
}

// Total revenue for a segment over its entire history; 0 flags a segment with
// no recorded data.
export async function fetchSegmentTotalRevenue(
    segmentId: string
): Promise<number> {
    const response = await querySegmentData<SegmentBrandRevenueRow>({
        segmentIds: segmentId ? [segmentId] : [],
        metrics: [REVENUE_METRIC],
        limit: 1,
    })
    return revenueOf(response.data.results[0])
}

// Revenue for a single brand in a segment over the window.
export async function fetchSegmentBrandPeriodRevenue({
    segmentId,
    brand,
    reportDateMin,
    reportDateMax,
}: {
    segmentId: string
    brand: string
    reportDateMin: string
    reportDateMax: string
}): Promise<number> {
    const response = await querySegmentData<SegmentBrandRevenueRow>({
        segmentIds: segmentId && brand ? [segmentId] : [],
        dimensions: [BRAND_DIMENSION],
        metrics: [REVENUE_METRIC],
        predicate: brand
            ? { field: BRAND_DIMENSION, operator: '_in', value: [brand] }
            : undefined,
        dateRange: { reportDateMin, reportDateMax },
        limit: 1,
    })
    return revenueOf(response.data.results[0])
}
